package co.maytes.standupbot.dashboard.features.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.MutationObserver
import org.w3c.dom.asList

enum class GalleryState(val label: String, val forced: String?) {
    Rest("Rest", null),
    Hover("Hover", "hover"),
    Focus("Focus-visible", "focus"),
    Active("Active", "active"),
    Disabled("Disabled", null),
    Loading("Loading", null),
    Invalid("Invalid", null),
}

object ForcedStates {
    const val ATTRIBUTE = "data-force-state"

    private const val HOVER_MEDIA = "hover: hover"
    private val pseudoClass = Regex(":(hover|focus-visible|focus|active)(?![-\\w])")
    private val seen: dynamic = js("new WeakSet()")

    fun install() {
        val sheets = document.styleSheets
        for (index in 0 until sheets.length) {
            val sheet = sheets.item(index).asDynamic() ?: continue
            try {
                duplicate(sheet)
            } catch (_: Throwable) {
            }
        }
    }

    fun forcedSelector(selector: String): String? =
        if (pseudoClass.containsMatchIn(selector) && !selector.contains(ATTRIBUTE)) {
            pseudoClass.replace(selector) { match ->
                val state = if (match.groupValues[1] == "focus-visible") "focus" else match.groupValues[1]
                "[$ATTRIBUTE=\"$state\"]"
            }
        } else {
            null
        }

    private fun duplicate(container: dynamic) {
        val rules = container.cssRules
        var index = 0
        while (index < (rules.length as Int)) {
            val rule = rules[index]
            if (rule.selectorText != null) {
                val forced = if (seen.has(rule) as Boolean) null else forcedSelector(rule.selectorText as String)
                seen.add(rule)
                if (forced != null) {
                    container.insertRule("$forced { ${rule.style.cssText as String} }", index + 1)
                    seen.add(rules[index + 1])
                    index++
                }
            } else if (rule.cssRules != null) {
                val condition = rule.conditionText as String?
                if (condition != null && condition.contains(HOVER_MEDIA)) {
                    index += hoistRules(rule, container, index)
                } else {
                    duplicate(rule)
                }
            }
            index++
        }
    }

    private fun hoistRules(media: dynamic, container: dynamic, mediaIndex: Int): Int {
        if (seen.has(media) as Boolean) return 0
        seen.add(media)
        val inner = media.cssRules
        var inserted = 0
        for (position in 0 until (inner.length as Int)) {
            val rule = inner[position]
            if (rule.selectorText == null) continue
            val forced = forcedSelector(rule.selectorText as String) ?: continue
            inserted++
            container.insertRule("$forced { ${rule.style.cssText as String} }", mediaIndex + inserted)
            seen.add(container.cssRules[mediaIndex + inserted])
        }
        return inserted
    }

    fun apply(root: Element, state: GalleryState) {
        val value = state.forced
        val targets = listOf(root) + root.querySelectorAll("*").asList().filterIsInstance<Element>()
        targets.forEach { if (value != null) it.setAttribute(ATTRIBUTE, value) else it.removeAttribute(ATTRIBUTE) }
    }
}

private class CellHolder(var element: HTMLElement? = null)

@Composable
fun StateCell(state: GalleryState, content: @Composable () -> Unit) {
    val holder = remember { CellHolder() }
    Div(
        attrs = {
            style {
                property("display", "flex")
                property("align-items", "center")
                property("min-height", "56px")
                property("padding", "8px")
            }
            ref { element ->
                holder.element = element
                onDispose { holder.element = null }
            }
        },
    ) {
        content()
    }
    DisposableEffect(state) {
        val element = holder.element
        var observer: MutationObserver? = null
        if (element != null) {
            ForcedStates.apply(element, state)
            observer = MutationObserver { _, _ -> ForcedStates.apply(element, state) }
            val options: dynamic = js("({})")
            options.childList = true
            options.subtree = true
            observer.asDynamic().observe(element, options)
        }
        onDispose { observer?.disconnect() }
    }
}
